import {
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RingGeometry,
  SphereGeometry
} from 'three';
import { MinionMeshFactory } from './minionMeshFactory.js';
import { ChampionMeshFactory } from './championMeshFactory.js';
import { JungleMeshFactory } from './jungleMeshFactory.js';
import { addStructureSilhouetteMeshes } from './structureSilhouetteMeshes.js';

export class MeshFactory {
  static createBushMesh(x = 0, y = 0.75, z = 0, radius = 2.2) {
    // Unlit: MeshStandardMaterial recolors with proximity lights / camera.
    const mesh = new Mesh(
      new CylinderGeometry(radius, radius, 1.5, 16),
      new MeshBasicMaterial({
        color: 0x15803d,
        transparent: true,
        opacity: 0.7,
        depthWrite: false
      })
    );
    mesh.position.set(x, y, z);
    return mesh;
  }

  static createMountainMesh(x = -6, z = 0) {
    const mountainGroup = new Group();
    const rockMat = new MeshStandardMaterial({ color: 0x475569, roughness: 0.8 });

    const rock1 = new Mesh(new DodecahedronGeometry(2.2, 1), rockMat);
    rock1.position.set(x, 1.2, z);
    mountainGroup.add(rock1);

    const rock2 = new Mesh(new DodecahedronGeometry(1.6, 1), rockMat);
    rock2.position.set(x - 1.5, 1.0, z + 1.5);
    mountainGroup.add(rock2);

    return mountainGroup;
  }

  static createTurretMesh(turretEntity, hudUI, turretsUiContainer) {
    const group = new Group();
    const color = turretEntity.team === 'blue' ? 0x3b82f6 : 0xef4444;
    const type = turretEntity.type || 'turret';

    addStructureSilhouetteMeshes(group, turretEntity, color);

    group.position.set(turretEntity.x, 0, turretEntity.z);
    group.userData.structureType = type;
    group.userData.structureTier = turretEntity.tier || type;

    const plateDiv = document.createElement('div');
    plateDiv.className = 'turret-plate';
    plateDiv.innerHTML = `
      <div class="plate-name" style="color: ${turretEntity.team === 'blue' ? '#38bdf8' : '#ef4444'};">${turretEntity.name}</div>
      <div class="plate-hp-outer"><div class="${turretEntity.team === 'blue' ? 'blue-minion-hp-inner' : 'minion-hp-inner'}"></div></div>
    `;
    const hpOuter = plateDiv.querySelector('.plate-hp-outer');
    if (hudUI && hudUI.applyHpTicks) {
      hudUI.applyHpTicks(hpOuter, turretEntity.maxHp, 250);
    }
    if (turretsUiContainer) {
      turretsUiContainer.appendChild(plateDiv);
    }

    return { group, plateDiv, hpInner: plateDiv.querySelector('.blue-minion-hp-inner, .minion-hp-inner') };
  }

  /**
   * Creates custom 3D minion mesh (Melee, Ranged, Cannon, Super) using MinionMeshFactory.
   */
  static createMinionMesh(minionDomain, hudUI, minionsUiContainer) {
    const minionGroup = new Group();
    const team = minionDomain.team || 'blue';
    const type = minionDomain.minionType || 'melee';

    let modelMesh;
    if (type === 'cannon') {
      modelMesh = MinionMeshFactory.createCannonMinionMesh(team);
    } else if (type === 'ranged') {
      modelMesh = MinionMeshFactory.createRangedMinionMesh(team);
    } else if (type === 'super') {
      modelMesh = MinionMeshFactory.createSuperMinionMesh(team);
    } else {
      modelMesh = MinionMeshFactory.createMeleeMinionMesh(team);
    }
    minionGroup.add(modelMesh);

    const color = team === 'blue' ? 0x3b82f6 : 0xef4444;
    const radiusUnits = (minionDomain.radius || 16) / 16;
    const baseRing = new Mesh(
      new RingGeometry(radiusUnits * 0.8, radiusUnits, 16).rotateX(-Math.PI / 2),
      new MeshBasicMaterial({ color, side: DoubleSide, transparent: true, opacity: 0.5 })
    );
    baseRing.position.y = 0.02;
    minionGroup.add(baseRing);

    const plateDiv = document.createElement('div');
    plateDiv.className = 'minion-plate';
    const hpInnerClass = team === 'blue' ? 'blue-minion-hp-inner' : 'minion-hp-inner';
    plateDiv.innerHTML = `
      <div class="plate-hp-outer"><div class="${hpInnerClass}"></div></div>
    `;

    const hpOuter = plateDiv.querySelector('.plate-hp-outer');
    if (hudUI && hudUI.applyHpTicks) {
      hudUI.applyHpTicks(hpOuter, minionDomain.maxHp, 50);
    }

    const hpInner = plateDiv.querySelector(`.${hpInnerClass}`);
    if (minionsUiContainer) {
      minionsUiContainer.appendChild(plateDiv);
    }

    return { group: minionGroup, plateDiv, hpOuter, hpInner };
  }

  static createFrostArrowMesh(isSkillshot = false) {
    const arrowGroup = new Group();
    const arrowMat = new MeshBasicMaterial({ color: isSkillshot ? 0x38bdf8 : 0x60a5fa });
    const crystalMat = new MeshBasicMaterial({ color: isSkillshot ? 0x93c5fd : 0xbfdbfe });

    const scale = isSkillshot ? 0.85 : 1.0;
    const shaft = new Mesh(new CylinderGeometry(0.04 * scale, 0.04 * scale, 1.2 * scale, 8).rotateX(Math.PI / 2), arrowMat);
    arrowGroup.add(shaft);

    const tip = new Mesh(new ConeGeometry(0.12 * scale, 0.4 * scale, 6).rotateX(Math.PI / 2), crystalMat);
    tip.position.z = 0.6 * scale;
    arrowGroup.add(tip);

    return arrowGroup;
  }

  static createUltimateArrowMesh() {
    const ultGroup = new Group();
    const crystalMat = new MeshStandardMaterial({ color: 0xa855f7, emissive: 0x93c5fd, roughness: 0.1 });
    const coreMat = new MeshBasicMaterial({ color: 0xffffff });

    const shaft = new Mesh(new CylinderGeometry(0.18, 0.18, 3.2, 12).rotateX(Math.PI / 2), crystalMat);
    ultGroup.add(shaft);

    const tip = new Mesh(new ConeGeometry(0.5, 1.2, 8).rotateX(Math.PI / 2), coreMat);
    tip.position.z = 1.6;
    ultGroup.add(tip);

    const aura = new Mesh(new SphereGeometry(1.2, 16, 16), new MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.4 }));
    ultGroup.add(aura);

    return ultGroup;
  }

  static createHawkMesh() {
    const hawkGroup = new Group();
    const coreMat = new MeshBasicMaterial({ color: 0xffffff });

    const body = new Mesh(new ConeGeometry(0.35, 1.4, 8).rotateX(Math.PI / 2), coreMat);
    hawkGroup.add(body);

    const visionField = new Mesh(
      new RingGeometry(3.9, 4.0, 32).rotateX(-Math.PI / 2),
      new MeshBasicMaterial({ color: 0x38bdf8, side: DoubleSide, transparent: true, opacity: 0.4 })
    );
    visionField.position.y = -2.0;
    hawkGroup.add(visionField);

    return hawkGroup;
  }

  static createTurretShotMesh(team) {
    const color = team === 'blue' ? 0x38bdf8 : 0xef4444;
    return new Mesh(
      new SphereGeometry(0.4, 16, 16),
      new MeshBasicMaterial({ color })
    );
  }

  static createJungleCampMesh(campState, hudUI, container) {
    const campGroup = new Group();
    const monsterMeshes = [];
    const cid = campState.id;

    if (campState.monsters && campState.monsters.length > 0) {
      campState.monsters.forEach((monster, idx) => {
        const monsterGroup = new Group();
        let modelMesh;

        if (cid === 'blue_golem') {
          modelMesh = idx === 0 ? JungleMeshFactory.createBlueBuffMesh() : JungleMeshFactory.createScuttleCrabMesh();
        } else if (cid === 'red_lizard') {
          modelMesh = idx === 0 ? JungleMeshFactory.createRedBuffMesh() : JungleMeshFactory.createScuttleCrabMesh();
        } else if (cid === 'dragon') {
          modelMesh = JungleMeshFactory.createDragonMesh();
        } else if (cid === 'baron') {
          modelMesh = JungleMeshFactory.createBaronMesh();
        } else if (cid === 'gromp') {
          modelMesh = JungleMeshFactory.createGrompMesh();
        } else if (cid === 'wolves') {
          modelMesh = idx === 0 ? JungleMeshFactory.createWolfMesh('alpha') : JungleMeshFactory.createWolfMesh('small');
        } else if (cid === 'wraiths') {
          modelMesh = JungleMeshFactory.createRazorbeakMesh();
          if (idx > 0) modelMesh.scale.set(0.65, 0.65, 0.65);
        } else {
          modelMesh = idx === 0 ? JungleMeshFactory.createBlueBuffMesh() : JungleMeshFactory.createScuttleCrabMesh();
        }

        monsterGroup.add(modelMesh);

        if (modelMesh.userData && typeof modelMesh.userData.update === 'function') {
          monsterGroup.userData.update = modelMesh.userData.update;
        }

        const color = cid === 'blue_golem' ? 0x3b82f6 : (cid === 'red_lizard' || cid === 'dragon' ? 0xef4444 : (cid === 'baron' ? 0xa855f7 : 0x0d9488));
        const ring = new Mesh(
          new RingGeometry(0.8, 0.95, 16).rotateX(-Math.PI / 2),
          new MeshBasicMaterial({ color, side: DoubleSide, transparent: true, opacity: 0.5 })
        );
        ring.position.y = 0.02;
        monsterGroup.add(ring);

        monsterGroup.position.set(monster.x, 0, monster.z);
        campGroup.add(monsterGroup);

        monsterMeshes.push({ entity: monster, group: monsterGroup });
      });
    }

    const plateDiv = document.createElement('div');
    plateDiv.className = 'minion-plate';
    plateDiv.innerHTML = `
      <div class="plate-name" style="color: #cbd5e1; font-size: 10px; text-shadow: 0 0 4px #000;">${campState.spec ? campState.spec.name : cid}</div>
      <div class="plate-hp-outer"><div class="minion-hp-inner" style="background: #a855f7;"></div></div>
    `;

    if (container) {
      container.appendChild(plateDiv);
    }

    return { group: campGroup, monsterMeshes, plateDiv, hpInner: plateDiv.querySelector('.minion-hp-inner') };
  }

  static updateMeshAnimations(group, dt, elapsed) {
    if (group && group.userData && typeof group.userData.update === 'function') {
      group.userData.update(dt, elapsed);
    }
  }
}

